import { Table } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { t } from 'i18next'
import { useMemo } from 'react'
import Moment from 'react-moment'
import { useNavigate, useParams } from 'react-router-dom'
import { styled } from 'styled-components'
import {
  Badge,
  ListPageLayout,
  SectionLoadingState,
  TableBoldColumnContainer,
} from '../../../components'
import {
  BadgeColors,
  NO_DATA_PLACEHOLDER,
  WorkspaceDocumentSetStatus,
  dateFormat,
  workspaceDocumentSetAllOwnersStatus,
  workspaceDocumentSetStatuses,
} from '../../../constants/constants'
import {
  useRiskReliefDeviations,
  useUsers,
  useWorkspaceDetails,
} from '../../../hooks'
import { SearchQuery } from '../../../store/reducers/searchReviewReducer'
import { routePaths } from '../../RootPage'
import { workspaceTabs, workspacePanelTypes } from '../WorkspaceDetailsPage'

const SectionLoadingStateContainer = styled.div`
  display: flex;
  height: calc(100vh - 224px);
`

interface ExtendedSearchQuery extends SearchQuery {
  key: string
  ownerName: string
  ownerId: string
  searchReviewTitle?: string
  searchQueryText?: string
  publicationDate?: string
  deviationStatus: WorkspaceDocumentSetStatus
}

interface WorkspaceDeviationsPanelProps {
  statusFilter: WorkspaceDocumentSetStatus
  deviationOwner: string
}

const WorkspaceDeviationsPanel = ({
  statusFilter,
  deviationOwner,
}: WorkspaceDeviationsPanelProps) => {
  const navigate = useNavigate()
  const { workspaceId } = useParams()
  const { selectedWorkspace } = useWorkspaceDetails({
    workspaceId,
    preventFetch: true,
  })
  const { deviationList, loading, searchReviewBatchList } =
    useRiskReliefDeviations({
      workspaceId,
    })
  const { findUser } = useUsers({})

  const formattedData: ExtendedSearchQuery[] = useMemo(() => {
    if (!searchReviewBatchList?.length) return []
    const queryDeviations =
      selectedWorkspace?.config?.riskReliefConfig?.queryDeviations || []

    const extendedQueries: ExtendedSearchQuery[] = deviationList
      .filter((query) => query !== undefined)
      .map((query) => {
        const searchReview = searchReviewBatchList.find(
          (review) => review.id === query.literatureReview
        )

        const ownerId = query.searchActionBy?.id ?? ''
        const ownerName = findUser(ownerId)?.displayName ?? ''

        const deviationStatus =
          queryDeviations.find((d) => d.searchQueryId === query.id)
            ?.deviationStatus ?? ''

        return {
          ...query,
          ownerId,
          ownerName,
          searchReviewTitle: searchReview?.title ?? '',
          searchQueryText: query.searchText,
          publicationDate: query.searchDate,
          key: `${query.searchSource?.id}-${query.id}`,
          deviationStatus: deviationStatus as WorkspaceDocumentSetStatus,
        } as ExtendedSearchQuery
      })

    return extendedQueries.filter((item) => {
      // match status and owner!
      const statusMatches =
        statusFilter === workspaceDocumentSetStatuses.ALL ||
        item.deviationStatus === statusFilter

      const ownerMatches =
        deviationOwner === workspaceDocumentSetAllOwnersStatus ||
        item.ownerId === deviationOwner

      return statusMatches && ownerMatches
    })
  }, [
    findUser,
    selectedWorkspace,
    statusFilter,
    deviationOwner,
    deviationList,
    searchReviewBatchList,
  ])

  const columns: ColumnsType<ExtendedSearchQuery> = [
    {
      title: t('workspaceDetailsPage.workspaceDeviationsPanel.searchQuery'),
      dataIndex: 'searchQueryText',
      key: 'searchQueryText',
      width: '40%',
      sorter: (a: ExtendedSearchQuery, b: ExtendedSearchQuery) =>
        a.searchQueryText && b.searchQueryText
          ? a.searchQueryText.localeCompare(b.searchQueryText)
          : -1,
      render: (text, record) => (
        <TableBoldColumnContainer>
          {text || NO_DATA_PLACEHOLDER}
        </TableBoldColumnContainer>
      ),
    },
    {
      title: t(
        'workspaceDetailsPage.workspaceDeviationsPanel.historicalReview'
      ),
      dataIndex: 'searchReviewTitle',
      key: 'searchReviewTitle',
      width: '20%',
      sorter: (a: ExtendedSearchQuery, b: ExtendedSearchQuery) =>
        a.searchReviewTitle && b.searchReviewTitle
          ? a.searchReviewTitle.localeCompare(b.searchReviewTitle)
          : -1,
      render: (text) => text || NO_DATA_PLACEHOLDER,
    },
    {
      title: t('Date'),
      dataIndex: 'publicationDate',
      key: 'publicationDate',
      className: 'max-with-deviation',
      sorter: (a: ExtendedSearchQuery, b: ExtendedSearchQuery) =>
        a.publicationDate && b.publicationDate
          ? a.publicationDate.localeCompare(b.publicationDate)
          : -1,
      render: (textDate) => (
        <>
          {textDate ? (
            <Moment local format={dateFormat.PRIMARY}>
              {textDate}
            </Moment>
          ) : (
            NO_DATA_PLACEHOLDER
          )}
        </>
      ),
    },
    {
      title: t('Owner'),
      dataIndex: 'ownerName',
      key: 'ownerName',
      className: 'max-with-deviation',
      sorter: (a: ExtendedSearchQuery, b: ExtendedSearchQuery) =>
        a.ownerName && b.ownerName
          ? a.ownerName.localeCompare(b.ownerName)
          : -1,
      render: (text) => <>{text || NO_DATA_PLACEHOLDER}</>,
    },
    {
      title: t('Status'),
      dataIndex: 'deviationStatus',
      key: 'deviationStatus',
      sorter: (a: ExtendedSearchQuery, b: ExtendedSearchQuery) =>
        a.deviationStatus && b.deviationStatus
          ? a.deviationStatus.localeCompare(b.deviationStatus)
          : -1,
      render: (text) => (
        <>
          {
            <Badge
              color={
                text === workspaceDocumentSetStatuses.CLOSED
                  ? BadgeColors.STATUS_CLOSED
                  : BadgeColors.STATUS_OPENED
              }
            >
              {t(
                `workspaceDetailsPage.documentSetStatus.${
                  text || workspaceDocumentSetStatuses.OPENED
                }`
              )}
            </Badge>
          }
        </>
      ),
    },
  ]

  const handleClick = (record: ExtendedSearchQuery) => {
    navigate(
      `${routePaths.WORKSPACE_DETAILS}/${workspaceId}/${workspacePanelTypes.DEVIATIONS}/${workspaceTabs.INFO}/${record.id}`
    )
  }

  return loading ? (
    <SectionLoadingStateContainer>
      <SectionLoadingState />
    </SectionLoadingStateContainer>
  ) : (
    <ListPageLayout>
      <Table
        data-testid="data-sources-list-table"
        rowClassName="page-list-table-row clickable"
        columns={columns}
        dataSource={formattedData}
        pagination={false}
        scroll={{ y: 'calc(100vh - 170px)' }}
        onRow={(record) => ({
          onClick: () => handleClick(record),
        })}
      />
    </ListPageLayout>
  )
}

export default WorkspaceDeviationsPanel
